import React, { useState } from "react";
import { Tent, Laptop } from "lucide-react";
import { useLang } from "../LanguageContext";
import FormationPresentiel from "../components/formation/FormationPresentiel";
import FormationOnline from "../components/formation/FormationOnline";
import FormationFinancing from "../components/formation/FormationFinancing";

const FEATURE_IMG = "https://customer-assets-4nw71qhi.emergentagent.net/job_ecole-pleine-nature/artifacts/xar4fz0d_kitchen.webp";

export default function Formation() {
  const { t, lang } = useLang();
  const [format, setFormat] = useState("presentiel");

  return (
    <div data-testid="page-formation">
      {/* HERO */}
      <section className="container-page pt-16 sm:pt-24 pb-8">
        <div className="grid md:grid-cols-[minmax(0,1fr)_14rem] gap-8 md:gap-10 items-start">
          <div>
            <p className="eyebrow mb-4">{t("formation.eyebrow")}</p>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-medium text-ink max-w-3xl leading-tight">
              {t("formation.hero_title")}
            </h1>
            <p className="mt-4 text-lg text-ink-muted max-w-2xl leading-relaxed">
              {t("formation.hero_subtitle")}
            </p>
          </div>
          <div className="rounded-[1.75rem] overflow-hidden shadow-soft bg-cream-100 shrink-0">
            <img
              src={FEATURE_IMG}
              alt="École Pleine Nature — un temps d’apprentissage encadré"
              className="w-full h-auto block"
              loading="lazy"
            />
          </div>
        </div>
      </section>

      {/* FORMAT TOGGLE */}
      <section className="container-page pb-10">
        <div
          className="inline-flex flex-col sm:flex-row gap-3 p-1.5 rounded-[1.25rem] bg-cream-200/70 border border-cream-300"
          role="tablist"
          aria-label={t("formation.hero_title")}
          data-testid="formation-format-toggle"
        >
          <button
            type="button"
            role="tab"
            aria-selected={format === "presentiel"}
            onClick={() => setFormat("presentiel")}
            className={`flex items-center gap-3 rounded-[0.9rem] px-5 py-3.5 text-left transition-colors ${
              format === "presentiel" ? "bg-cream-50 shadow-soft" : "hover:bg-cream-50/60"
            }`}
            data-testid="formation-format-presentiel"
          >
            <span
              className={`w-9 h-9 rounded-full grid place-items-center shrink-0 ${
                format === "presentiel" ? "bg-forest text-cream-50" : "bg-cream-300 text-ink-muted"
              }`}
            >
              <Tent size={17} aria-hidden />
            </span>
            <span>
              <span className="block font-heading text-sm font-medium text-ink">
                {t("formation.format_presentiel_label")}
              </span>
              <span className="block text-xs text-ink-muted mt-0.5">
                {t("formation.format_presentiel_desc")}
              </span>
            </span>
          </button>

          <button
            type="button"
            role="tab"
            aria-selected={format === "online"}
            onClick={() => setFormat("online")}
            className={`flex items-center gap-3 rounded-[0.9rem] px-5 py-3.5 text-left transition-colors ${
              format === "online" ? "bg-cream-50 shadow-soft" : "hover:bg-cream-50/60"
            }`}
            data-testid="formation-format-online"
          >
            <span
              className={`w-9 h-9 rounded-full grid place-items-center shrink-0 ${
                format === "online" ? "bg-forest text-cream-50" : "bg-cream-300 text-ink-muted"
              }`}
            >
              <Laptop size={17} aria-hidden />
            </span>
            <span>
              <span className="block font-heading text-sm font-medium text-ink">
                {t("formation.format_online_label")}
              </span>
              <span className="block text-xs text-ink-muted mt-0.5">
                {t("formation.format_online_desc")}
              </span>
            </span>
          </button>
        </div>
      </section>

      {format === "presentiel" ? (
        <FormationPresentiel t={t} lang={lang} />
      ) : (
        <FormationOnline t={t} lang={lang} />
      )}

      <FormationFinancing t={t} />
    </div>
  );
}
